import { Theme, useTheme } from '@chronosphereio/chrono-ui';
import { useMemo } from 'react';
import { useSeriesColorGenerator } from '@/components';
import type { SeriesColorGenerator, StatusHistoryLabelGrouping, StatusHistoryLabelMappingEntry } from '@/core';

type SemanticCategory = 'success' | 'error' | 'warning' | 'info' | 'grey';

/**
 * Static map from well-known status label values to theme palette categories.
 * Resolved to actual hex colors at call time via `theme.palette[category].main`.
 */
const SEMANTIC_STATE_CATEGORIES: Record<string, SemanticCategory> = {
  // success / healthy states
  running: 'success',
  healthy: 'success',
  succeeded: 'success',
  success: 'success',
  active: 'success',
  up: 'success',
  ready: 'success',
  ok: 'success',
  available: 'success',

  // error / failure states
  failed: 'error',
  error: 'error',
  critical: 'error',
  crash: 'error',
  down: 'error',
  crashloopbackoff: 'error',
  oomkilled: 'error',

  // warning / transitional states
  pending: 'warning',
  warning: 'warning',
  degraded: 'warning',
  waiting: 'warning',
  terminating: 'warning',

  // informational states
  unknown: 'grey',
  terminated: 'grey',
  completed: 'info',
  evicted: 'grey',
};

function resolveSemanticColor(labelValue: string, theme: Theme): string | undefined {
  const category = SEMANTIC_STATE_CATEGORIES[labelValue.toLowerCase()];
  if (!category) return undefined;
  if (category === 'grey')
    return theme.featureFlags.panwRebrandStubYear ? theme.palette.status.default.main : theme.palette.grey[500];
  return theme.palette[category].main;
}

/**
 * Single source of truth for label color resolution: merges persisted mappings
 * with auto colors (semantic + consistent palette) for values observed in `allSeries`.
 *
 * Returns a spec-shaped `StatusHistoryLabelGrouping` with resolved mappings
 * patched in, so both the editor and the panel can consume the same object.
 */
export function useResolvedLabelColorMappings({
  labelGrouping,
  allSeries,
  colorByLabelActive,
}: {
  labelGrouping: StatusHistoryLabelGrouping | undefined;
  allSeries: Array<{ labels?: Record<string, string> }>;
  colorByLabelActive: boolean;
}): {
  resolvedLabelGrouping: StatusHistoryLabelGrouping | undefined;
} {
  const { theme } = useTheme();
  const seriesColorGenerator = useSeriesColorGenerator();
  const colorBy = labelGrouping?.color_by;
  const enabled = colorByLabelActive && !!colorBy;

  const labelValues = useMemo(() => {
    if (!colorBy) return [];
    const values = new Set<string>();
    for (const s of allSeries) {
      const v = s.labels?.[colorBy];
      if (v !== undefined) values.add(v);
    }
    return Array.from(values).sort();
  }, [allSeries, colorBy]);

  const resolvedLabelGrouping = useMemo((): StatusHistoryLabelGrouping | undefined => {
    if (!labelGrouping?.color_by) return undefined;
    if (!enabled) return labelGrouping;
    const mappings = resolveLabelColorMappings({
      labelValues,
      mappings: labelGrouping.color_mapping?.mappings,
      theme,
      seriesColorGenerator,
    });
    return {
      ...labelGrouping,
      color_mapping: {
        ...labelGrouping.color_mapping,
        mappings,
      },
    };
  }, [enabled, labelGrouping, labelValues, theme, seriesColorGenerator]);

  return { resolvedLabelGrouping };
}

interface ResolveLabelColorMappingsArgs {
  labelValues: string[];
  mappings?: StatusHistoryLabelMappingEntry[];
  theme: Theme;
  seriesColorGenerator: SeriesColorGenerator;
}

/**
 * Returns fully-resolved mappings (every entry has a `color`) by merging
 * explicit mappings with auto-derived colors for any observed label values
 * that are not explicitly configured.
 *
 * Color derivation priority:
 * 1. Explicit `mapping.color` (user-chosen)
 * 2. Semantic color (well-known state name -> theme palette)
 * 3. Consistent palette via `SeriesColorGenerator` (wraps via modulo, never exhausts)
 */
export function resolveLabelColorMappings({
  labelValues,
  mappings,
  theme,
  seriesColorGenerator,
}: ResolveLabelColorMappingsArgs): StatusHistoryLabelMappingEntry[] {
  const orderedValues = Array.from(new Set(labelValues));
  const explicitMappings = mappings ?? [];
  const knownValues = new Set(explicitMappings.map((mapping) => mapping.label_value));

  const resolvedMappings: StatusHistoryLabelMappingEntry[] = explicitMappings.map((mapping) => ({
    ...mapping,
    color: mapping.color ?? deriveColor(mapping.label_value, theme, seriesColorGenerator),
  }));

  for (const labelValue of orderedValues) {
    if (knownValues.has(labelValue)) continue;
    resolvedMappings.push({
      label_value: labelValue,
      color: deriveColor(labelValue, theme, seriesColorGenerator),
    });
  }

  return resolvedMappings;
}

function deriveColor(labelValue: string, theme: Theme, seriesColorGenerator: SeriesColorGenerator): string {
  return (
    resolveSemanticColor(labelValue, theme) ??
    // Same normalization as semantic lookup so casing-only differences share one palette color.
    seriesColorGenerator.getConsistentSeriesNameColor(labelValue.toLowerCase(), theme.palette.primary.main)
  );
}
